import { useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export const driverPhotoUrl = (id, v) => (id && v ? `${process.env.REACT_APP_BACKEND_URL}/api/drivers/${id}/photo?v=${v}` : null);

export const DriverAvatar = ({ id, v, name, size = "h-12 w-12", testId = "driver-avatar" }) => {
  const [failed, setFailed] = useState(false);
  const url = driverPhotoUrl(id, v);
  if (!url || failed) {
    const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return <span data-testid={`${testId}-initials`} className={`${size} flex shrink-0 items-center justify-center rounded-full bg-blue-700 font-bold text-white`}>{initials}</span>;
  }
  return <img data-testid={testId} src={url} alt={name} onError={() => setFailed(true)} className={`${size} shrink-0 rounded-full object-cover ring-2 ring-amber-400`} />;
};

const resize = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(img.width, img.height);
      const c = document.createElement("canvas");
      c.width = c.height = 320;
      c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 320, 320);
      URL.revokeObjectURL(img.src);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => reject(new Error("That file isn't a photo we can read"));
    img.src = URL.createObjectURL(file);
  });

export const DriverPhotoEditor = () => {
  const { user, refresh } = useAuth();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) run(async () => api.put("/auth/me/photo", { image: await resize(f) }), "Photo updated");
  };
  return (
    <div className="flex items-center gap-4" data-testid="driver-photo-editor">
      <DriverAvatar id={user.id} v={user.photo_v} name={user.name} size="h-20 w-20" testId="my-photo" />
      <div className="space-y-2">
        <input ref={input} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="driver-photo-input" />
        <button type="button" disabled={busy} onClick={() => input.current.click()} data-testid="driver-photo-upload-button" className="flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 text-xs font-bold text-slate-900 hover:bg-amber-300 disabled:opacity-60">
          <Camera className="h-3.5 w-3.5" /> {busy ? "Uploading…" : user.photo_v ? "Change photo" : "Add photo"}
        </button>
        {user.photo_v && (
          <button type="button" disabled={busy} onClick={() => run(() => api.delete("/auth/me/photo"), "Photo removed")} data-testid="driver-photo-remove-button" className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        )}
        <p className="text-[11px] text-slate-500">A clear face photo helps passengers find you at pickup.</p>
      </div>
    </div>
  );
};
